import firebaseService from '../firebase.service';
import * as XLSX from 'xlsx';

class OptimizedDataService {
  constructor() {
    this.cache = null;
  }

  // Set the cache context
  setCache(cacheContext) {
    this.cache = cacheContext;
  }

  // Get all books for BookList (lightweight data only)
  async getAllBooks(project) {
    if (!this.cache) {
      throw new Error('Cache context not set');
    }

    // Check cache first
    if (this.cache.isCacheValid(project, 'books_list')) {
      // Using cached books
      return this.cache.getCachedData(project, 'books_list');
    }

    try {
      // Loading fresh data
      
      // Try optimized metadata file first
      const metadataPath = `${project.toUpperCase()}/books/metadata.json`;
      // Trying metadata file
      const metadataUrl = await firebaseService.getFileUrl(metadataPath);
      
      if (metadataUrl) {
        // Found metadata file, loading
        const response = await fetch(metadataUrl, { cache: 'no-store' });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const metadata = await response.json();
        // Loaded books from metadata
        
        // Cache the lightweight list
        this.cache.setCachedData(project, 'books_list', metadata.books || []);
        
        return metadata.books || [];
      } else {
        // No metadata file found, falling back to Excel
        // Fallback to legacy Excel file for BookList display
        return await this.getAllBooksFromExcel(project);
      }
    } catch (error) {
      // Error getting all books
      
      // Try fallback to Excel if metadata fails
      try {
        // Trying Excel fallback
        return await this.getAllBooksFromExcel(project);
      } catch (excelError) {
        // Excel fallback also failed
        throw new Error(`Failed to load books for ${project}: ${error.message}`);
      }
    }
  }

  // Fallback method to load books from Excel file (for BookList display only)
  async getAllBooksFromExcel(project) {
    try {
      // Loading from Excel
      
      const excelPath = `${project.toUpperCase()}/data/booklist.xlsx`;
      // Excel path
      const excelUrl = await firebaseService.getFileUrl(excelPath);
      
      if (excelUrl) {
        // Found Excel file, loading
        const response = await fetch(excelUrl);
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        
        const headers = jsonData[0];
        const books = [];
        
        // Excel headers
        
        // Find code column index
        let codeColumnIndex = headers.findIndex(header => 
          header && header.toLowerCase().includes('code')
        );
        
        if (codeColumnIndex === -1) {
          codeColumnIndex = headers.findIndex(header => 
            header && (
              header.toLowerCase().includes('book_code') ||
              header.toLowerCase().includes('bookcode') ||
              header.toLowerCase().includes('id') ||
              header.toLowerCase().includes('identifier')
            )
          );
        }
        
        // Code column index
        // Excel headers
        
        // Process data rows (skip header row)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length > 0) {
            const book = {};
            
            // Map each column to book property
            headers.forEach((header, index) => {
              if (header && row[index] !== undefined) {
                book[header.trim()] = String(row[index]).trim();
              }
            });
            
            // Set code from identified column
            if (codeColumnIndex !== -1 && row[codeColumnIndex]) {
              book.code = String(row[codeColumnIndex]).trim();
            }
            
            // Map Excel headers to standard property names
            if (book['NAME (EN)']) book.name_en = book['NAME (EN)'];
            if (book['NAME (TC)']) book.name_tc = book['NAME (TC)'];
            if (book['NAME (SC)']) book.name_sc = book['NAME (SC)'];
            if (book['PUBLISHER (EN)']) book.publisher_en = book['PUBLISHER (EN)'];
            if (book['PUBLISHER (TC)']) book.publisher_tc = book['PUBLISHER (TC)'];
            if (book['PUBLISHER (SC)']) book.publisher_sc = book['PUBLISHER (SC)'];
            if (book['AUTHOR (EN)']) book.author_en = book['AUTHOR (EN)'];
            if (book['AUTHOR (TC)']) book.author_tc = book['AUTHOR (TC)'];
            if (book['AUTHOR (SC)']) book.author_sc = book['AUTHOR (SC)'];
            if (book['COMPANY']) book.company = book['COMPANY'];
            
            // Handle redirect URL mapping - check multiple possible column names
            if (book['REDIRECT']) {
              book.redirect_link = book['REDIRECT'];
              // Found redirect
            } else if (book['redirect_link']) {
              // Found redirect_link
            } else if (book['REDIRECT_LINK']) {
              book.redirect_link = book['REDIRECT_LINK'];
              // Found REDIRECT_LINK
            } else if (book['redirect']) {
              book.redirect_link = book['redirect'];
              // Found redirect
            }
            
            // Only add books that have a name or valid code
            if (book.name_en || book.name_tc || book.name_sc || 
                (book.code && /^[A-Z]{2}-[A-Z]{2}-[A-Z]{2}-[A-Z0-9]{3}$/.test(book.code))) {
              books.push(book);
            }
          }
        }
        
        // Loaded books from Excel
        
        // Cache the result
        this.cache.setCachedData(project, 'books_list', books);
        
        return books;
      } else {
        throw new Error(`Excel file not found at path: ${excelPath}`);
      }
    } catch (error) {
      // Error loading books from Excel
      throw error;
    }
  }

  // Get individual book details (full data)
  async getBook(project, bookCode) {
    if (!this.cache) {
      throw new Error('Cache context not set');
    }


    // Check cache first
    const cachedBook = this.cache.findCachedItem(project, 'books_detail', bookCode);
    if (cachedBook) {
      return cachedBook;
    }

    try {
      // Load individual book JSON file
      const bookPath = `${project.toUpperCase()}/books/${bookCode}.json`;
      const bookUrl = await firebaseService.getFileUrl(bookPath);
      
      if (bookUrl) {
        const response = await fetch(bookUrl);
        if (response.ok) {
          const book = await response.json();
          
          // Cache the individual book
          this.cache.setCachedData(project, 'books_detail', [book]);
          
          return book;
        } else {
          return null;
        }
      } else {
        return null;
      }
    } catch (error) {
      // Error loading book
      return null;
    }
  }

  // Get all companies for CompanyList
  async getAllCompanies(project) {
    if (!this.cache) {
      throw new Error('Cache context not set');
    }

    if (this.cache.isCacheValid(project, 'companies')) {
      return this.cache.getCachedData(project, 'companies');
    }

    try {
      const excelPath = `${project.toUpperCase()}/data/company.xlsx`;
      const excelUrl = await firebaseService.getFileUrl(excelPath);

      if (excelUrl) {
        const response = await fetch(excelUrl);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length < 2) return [];

        const headers = jsonData[0];
        const companies = [];

        let codeColumnIndex = headers.findIndex(header =>
          header && header.toLowerCase().includes('code')
        );
        if (codeColumnIndex === -1) {
          codeColumnIndex = headers.findIndex(header =>
            header && (
              header.toLowerCase().includes('company_code') ||
              header.toLowerCase().includes('id') ||
              header.toLowerCase().includes('identifier')
            )
          );
        }

        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (row && row.length > 0) {
            const company = {};

            headers.forEach((header, index) => {
              if (header && row[index] !== undefined) {
                const key = header.trim();
                company[key] = String(row[index]).trim();

                const cleanHeader = key.toLowerCase();
                if (cleanHeader.includes('exhibitor') && (cleanHeader.includes('en') || cleanHeader.includes('english'))) {
                  company.exhibitor_en = String(row[index]).trim();
                } else if (cleanHeader.includes('exhibitor') && (cleanHeader.includes('tc') || cleanHeader.includes('traditional'))) {
                  company.exhibitor_tc = String(row[index]).trim();
                } else if (cleanHeader.includes('exhibitor') && (cleanHeader.includes('sc') || cleanHeader.includes('simplified'))) {
                  company.exhibitor_sc = String(row[index]).trim();
                } else if (cleanHeader.includes('contact') && cleanHeader.includes('name') && (cleanHeader.includes('en') || cleanHeader.includes('english'))) {
                  company.contact_person_name_en = String(row[index]).trim();
                } else if (cleanHeader.includes('contact') && cleanHeader.includes('name') && (cleanHeader.includes('tc') || cleanHeader.includes('traditional'))) {
                  company.contact_person_name_tc = String(row[index]).trim();
                } else if (cleanHeader.includes('contact') && cleanHeader.includes('name') && (cleanHeader.includes('sc') || cleanHeader.includes('simplified'))) {
                  company.contact_person_name_sc = String(row[index]).trim();
                } else if (cleanHeader.includes('contact') && cleanHeader.includes('email')) {
                  company.contact_person_email = String(row[index]).trim();
                } else if (cleanHeader.includes('website') || cleanHeader.includes('web_site')) {
                  company.company_website = String(row[index]).trim();
                } else if (cleanHeader.includes('redirect') || cleanHeader.includes('link') || cleanHeader.includes('url')) {
                  company.redirect_link = String(row[index]).trim();
                }
              }
            });

            if (codeColumnIndex !== -1 && row[codeColumnIndex]) {
              company.code = String(row[codeColumnIndex]).trim();
            }

            if (company.exhibitor_en || company.exhibitor_tc || company.exhibitor_sc || company.code) {
              companies.push(company);
            }
          }
        }

        this.cache.setCachedData(project, 'companies', companies);
        return companies;
      } else {
        return [];
      }
    } catch (error) {
      console.error(`Failed to load companies for ${project}:`, error);
      return [];
    }
  }

  // Get company details
  async getCompany(project, companyCode) {
    if (!this.cache) {
      throw new Error('Cache context not set');
    }

    // Check cache first
    const cachedCompany = this.cache.findCachedItem(project, 'companies', companyCode);
    if (cachedCompany) {
      return cachedCompany;
    }

    try {
      // Try individual company JSON file first
      const companyPath = `${project.toUpperCase()}/companies/${companyCode}.json`;
      const companyUrl = await firebaseService.getFileUrl(companyPath);
      
      if (companyUrl) {
        const response = await fetch(companyUrl);
        if (response.ok) {
          const company = await response.json();
          this.cache.setCachedData(project, 'companies', [company]);
          return company;
        }
      }

      // Fallback: load all companies from Excel and search
      const allCompanies = await this.getAllCompanies(project);
      return allCompanies.find(c => c.code === companyCode) || null;
    } catch (error) {
      return null;
    }
  }

  // Clear cache for a specific project
  clearProjectCache(project) {
    if (this.cache) {
      this.cache.clearCache(project, 'books_list');
      this.cache.clearCache(project, 'books_detail');
      this.cache.clearCache(project, 'companies');
    }
  }
}

// Create a singleton instance
const optimizedDataService = new OptimizedDataService();
export default optimizedDataService;
